// Plan projection — port of iOS `PlanProjection` (in PlanDimensions.swift).
// Maps world floor-plane meters (Vec2 = x, z) into SVG canvas points, fit-to-rect
// with padding and an optional rotation baked in (so the plan re-fits at any angle).

import type { Vec2 } from "./types";

export interface Point {
  x: number;
  y: number;
}

export class PlanProjection {
  readonly scale: number;
  readonly offset: Point;
  readonly rotation: number; // radians, applied about pivot BEFORE scale/offset
  readonly pivot: Vec2;

  constructor(
    boundsMin: Vec2,
    boundsMax: Vec2,
    size: { width: number; height: number },
    padding: number,
    rotation = 0
  ) {
    const pivot: Vec2 = {
      x: (boundsMin.x + boundsMax.x) / 2,
      y: (boundsMin.y + boundsMax.y) / 2,
    };
    this.rotation = rotation;
    this.pivot = pivot;

    // Fit the ROTATED content: rotate the bbox corners about the pivot and fit
    // their axis-aligned extent, so the whole plan stays inside the frame.
    const corners = [
      { x: boundsMin.x, y: boundsMin.y },
      { x: boundsMax.x, y: boundsMin.y },
      { x: boundsMax.x, y: boundsMax.y },
      { x: boundsMin.x, y: boundsMax.y },
    ].map((p) => PlanProjection.rotate(p, pivot, rotation));

    let mn = { ...corners[0] };
    let mx = { ...corners[0] };
    for (const p of corners) {
      mn = { x: Math.min(mn.x, p.x), y: Math.min(mn.y, p.y) };
      mx = { x: Math.max(mx.x, p.x), y: Math.max(mx.y, p.y) };
    }
    const extentX = mx.x - mn.x;
    const extentZ = mx.y - mn.y;
    const fit = Math.min(
      (size.width - padding * 2) / Math.max(extentX, 0.5),
      (size.height - padding * 2) / Math.max(extentZ, 0.5)
    );
    this.scale = fit;
    this.offset = {
      x: (size.width - extentX * fit) / 2 - mn.x * fit,
      y: (size.height - extentZ * fit) / 2 - mn.y * fit,
    };
  }

  private static rotate(p: Vec2, pivot: Vec2, a: number): Vec2 {
    if (a === 0) return p;
    const c = Math.cos(a);
    const s = Math.sin(a);
    const dx = p.x - pivot.x;
    const dy = p.y - pivot.y;
    return { x: dx * c - dy * s + pivot.x, y: dx * s + dy * c + pivot.y };
  }

  /** World floor-plane point → canvas point. */
  point(p: Vec2): Point {
    const r = PlanProjection.rotate(p, this.pivot, this.rotation);
    return { x: r.x * this.scale + this.offset.x, y: r.y * this.scale + this.offset.y };
  }

  /** Inverse — canvas point back to world floor-plane meters. */
  world(point: Point): Vec2 {
    const r: Vec2 = {
      x: (point.x - this.offset.x) / this.scale,
      y: (point.y - this.offset.y) / this.scale,
    };
    return PlanProjection.rotate(r, this.pivot, -this.rotation);
  }
}
